/**
 * The Schema surface — phase 2 §7.1 and §7.3.
 *
 * §7.1 puts Employee, Schema, and the client portal in "separate route trees
 * with separate shells, not one tree rendering conditionally", and this is the
 * second of the three. The gate above it (`_authed`) is shared, because
 * authentication is not what distinguishes them; everything below is not.
 *
 * **These screens are not scaffolding and are not deleted.** §7.3 is explicit:
 * [`phase-0-spec.md` §4.8](../../../../docs/specs/phase-0-spec.md) makes the
 * effective-access views the manual counterpart to the resolution test suite,
 * and acceptance criteria depend on them. What changed is that they are no
 * longer navigation for people doing their jobs.
 *
 * Nothing here is authorized by this file. The nav dims against the role
 * baseline the way the employee shell's does, and every route behind it is
 * authorized again by the API against the database (§4.7) — a person who types
 * the URL reaches screens whose every query answers for them, which is the
 * correct division rather than a gap.
 */

import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useMe } from '../api/queries.ts';
import { roleCarries } from '../components/capabilities.ts';
import { Badge, roleLabel } from '../components/ui/badge.tsx';
import { cn } from '../components/ui/cn.ts';
import { Loading } from '../components/ui/feedback.tsx';
import { ShellBar } from './shell-bar.tsx';
import {
  SIDEBAR_ID,
  SidebarResizeHandle,
  SidebarToggle,
  sidebarClasses,
  sidebarStyle,
  useSidebar,
} from './sidebar.tsx';

interface SchemaNavItem {
  to: string;
  label: string;
  description: string;
}

/**
 * §7.3's list, less the event log.
 *
 * The log is the one member of that sentence with no screen yet — §6.3 names the
 * audit view as one of its four consumers and nothing renders it — so it is
 * absent rather than stubbed. A nav item leading to an empty page would claim a
 * surface exists.
 */
const SCHEMA_NAV: SchemaNavItem[] = [
  { to: '/schema', label: 'Overview', description: 'What this surface is for' },
  {
    to: '/schema/spaces',
    label: 'Spaces & nodes',
    description: 'The raw tree, its depth counters, and the grants on it',
  },
  {
    to: '/schema/access',
    label: 'Effective access',
    description: 'Who can reach what, and which grant said so',
  },
];

function SchemaNavLink({ item }: { item: SchemaNavItem }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const active = item.to === '/schema' ? pathname === '/schema' : pathname.startsWith(item.to);

  return (
    <Link
      to={item.to}
      title={item.description}
      className={cn(
        'block rounded-md px-2.5 py-1.5 text-sm font-medium no-underline transition-colors',
        active ? 'bg-primary text-primary shadow-xs' : 'text-secondary hover:bg-secondary-hover',
      )}
    >
      {item.label}
    </Link>
  );
}

export function SchemaShell() {
  const { data, isPending } = useMe();
  const sidebar = useSidebar();

  if (isPending || !data) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Loading label="Resolving your session…" />
      </div>
    );
  }

  const user = data.user;
  const admin = roleCarries(user.role, 'audit.view');

  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      <aside id={SIDEBAR_ID} className={sidebarClasses(sidebar)} style={sidebarStyle(sidebar)}>
        <div className="flex h-full w-full flex-col lg:w-[var(--sidebar-width)]">
          <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-secondary px-3">
            <span className="flex min-w-0 items-center gap-2">
              <span className="grid size-7 shrink-0 place-items-center rounded-md bg-tertiary text-xs font-bold text-primary">
                S
              </span>
              <span className="truncate text-sm font-semibold text-primary">Schema</span>
            </span>
            <SidebarToggle sidebar={sidebar} />
          </div>

          <nav aria-label="Schema sections" className="flex flex-col gap-0.5 px-3 pt-4">
            {SCHEMA_NAV.map((item) => (
              <SchemaNavLink key={item.to} item={item} />
            ))}
          </nav>

          <div className="mt-auto flex flex-col gap-3 border-t border-secondary px-3 py-4">
            {admin ? null : (
              <p className="text-xs text-tertiary">
                Your role does not carry <span className="font-mono">audit.view</span>. These
                screens answer for you regardless — the API resolves every one of them against your
                own access.
              </p>
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-primary">{user.name}</p>
              <p className="mt-1.5">
                <Badge tone="brand">{roleLabel(user.role)}</Badge>
              </p>
            </div>
            {/* The way back, and the reason this is a shell rather than a section:
                a user in here has left the surface they do their job on. */}
            <Link to="/" className="text-sm link">
              ← Back to Matchbox OS
            </Link>
          </div>
        </div>
      </aside>

      <SidebarResizeHandle sidebar={sidebar} />

      <main className="flex min-w-0 flex-1 flex-col">
        <ShellBar sidebar={sidebar} />
        <div className="flex-1 px-4 py-6 sm:px-8 sm:py-8">
          <div className="mx-auto flex max-w-6xl flex-col gap-6">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
